export const ANALYTICS_EVENT_TYPE = {
  VIEW: 'View',
  ADD_TO_CART: 'AddToCart',
  REMOVE_FROM_CART: 'RemoveFromCart',
  PURCHASE: 'Purchase',
  IMPRESSION: 'Impression',
  CLICK: 'Click',
  HOME_PAGE_VIEW: 'HomePageView',
  BROWSING: 'Browsing',
  LOGIN: 'Login',
  SIGNUP: 'Signup',
  CUSTOM: 'Custom',
} as const;

export type AnalyticsEventType = (typeof ANALYTICS_EVENT_TYPE)[keyof typeof ANALYTICS_EVENT_TYPE];

export type AnalyticsService = 'klaviyo' | 'bigquery' | 'mixpanel' | 'paidmedia';

export interface ConsentState {
  analyticsStorage: 'granted' | 'denied';
  adStorage: 'granted' | 'denied';
}

export interface ProductContext {
  id?: string;
  quantity?: number;
  variant?: string;
  productUrl?: string;
}

export interface CheckoutContext {
  orderId?: string;
  totalItems?: number;
  totalQuantity?: number;
  total?: number;
  currency?: string;
  subtotal?: number;
  discount?: number;
  couponCode?: string;
  paymentType?: string;
  shippingMethod?: string;
  shippingCost?: number;
  cartProducts?: ProductContext[];
}

export interface InteractionContext {
  authenticationMethod?: string;
  buttonName?: string;
  sectionName?: string;
  filterValues?: Record<string, unknown>;
}

export interface CustomEventContext {
  /**
   * Channel-neutral canonical event name. Front sends this instead of
   * destination-specific legacy names.
   */
  eventName?: string;
  gtmEventName?: string;
  klaviyoMetricName?: string;
  params?: Record<string, unknown>;
}

export interface AnalyticsContext {
  clientId?: string;
  sessionId?: string;
  userId?: string;
  pagePath?: string;
  pageTitle?: string;
  pageReferrer?: string;
  pageType?: string;
  platform?: 'Web' | 'Android' | 'iOS';
  consent?: ConsentState;
  product?: ProductContext;
  checkout?: CheckoutContext;
  interaction?: InteractionContext;
  customEvent?: CustomEventContext;
  osName?: string;
  deviceModel?: string;
  appVersion?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  utmId?: string;
  gadSource?: string;
  gclid?: string;
  fbclid?: string;
  fbc?: string;
  fbp?: string;
  ttclid?: string;
  ttp?: string;
  msclkid?: string;
  wbraid?: string;
  gbraid?: string;
  mixpanelReplayId?: string;
}

export interface AnalyticsEventRequest {
  actorId: string;
  eventType: AnalyticsEventType;
  analytics: AnalyticsContext;
  services?: AnalyticsService[];
  productId?: string;
  orderId?: string;
  /** Client idempotency key — EA dedupes retries of the same logical event. */
  uniqueId?: string;
}
